/** ExhibitA is a list view in survey v2 submission form for users */
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Paper,
  Typography,
} from '@mui/material';
import SubHeadingTitle from '../../../layout/SubHeadingTitle';
import GenericDialog from '../../../GenericDialog';
import SubmissionDialog from './SubmissionDialog';
import SectionCaseSelector from './SectionCaseSelector';
import SubmissionReviewDialog from './SubmissionReviewDialog';
import QuestionInputFormPreview from '../QuestionInputFormPreview';
import ContextQuestionInputForm from '../ContextQuestionInputForm';
import QuestionInputForm from '../QuestionInputForm';
import {
  DigitalCase,
  DigitalQuestion,
  DigitalSection,
  DigitalSubmission,
  DigitalSubmissionMeta,
  Enrollment,
  FullSubmissionMap,
  QuestionCaseSetting,
  SurveySubmissionMeta,
  Trial,
} from '../../../../redux/types';
import {
  selectSettingsDictionaryByQuestionAndCase,
} from '../../../../redux/reducers/SurveyDataReducer';
import SectionOptOut from '../SectionOptOut';
import SectionOptOutPreview from '../SectionOptOutPreview';
import ReferenceLinks from '../ReferenceLinks';
import usePreventUnsavedTabClose from '../../../../hooks/survey/usePreventUnsavedTabClose';
import { QuestionSaveOp } from '../../../../hooks/survey/useQuestionSaveStatus';

type Props = {
  trial?: Trial,
  showEnrollmentsSubmissions?: boolean;
  isEditorPrivileged: boolean;
  userRole?: string;
  selectedCase?: DigitalCase;
  cases: DigitalCase[];
  questions: DigitalQuestion[];
  contextQuestions: DigitalQuestion[];
  selectedSection?: DigitalSection;
  sections: DigitalSection[];
  submissionsMapByQuestionAndCaseId: Record<number, Record<number, DigitalSubmission> | undefined>;
  contextSubmissionMapByQuestionId: Record<number, DigitalSubmission | undefined>;
  sectionSubmissionMeta?: DigitalSubmissionMeta;
  surveySubmissionMeta?: SurveySubmissionMeta;
  questionCaseSettings: QuestionCaseSetting[];
  settingsDictionary: ReturnType<typeof selectSettingsDictionaryByQuestionAndCase>;
  fullSubmissionMap: FullSubmissionMap;
  fullCtxSubmissionMap: Record<number, Record<number, DigitalSubmission>>;
  digitalSubmissionMetas: DigitalSubmissionMeta[];
  showContextQuestion: boolean;
  enrollment?: Enrollment;
  isPublicView: boolean;
  disabled: boolean;
  setShowContextQuestion: (show: boolean) => void;
  optOutFromSection: (trialId: number, sectionId: number,
    enrollmentId: number, isOptOut: boolean) => void;
  onAddOrUpdateDigitalSubmission: (submission: DigitalSubmission) => void;
  setSelectedDigitalSection: (sectionId?: number | undefined) => void;
  setSelectedCase: (c?: number | undefined) => void;
  finalizeSubmissions: () => Promise<void>;
  showErrorNotification: (message: string) => void;
}

type BootstrapQuestionFormProps = {
  isPublicView: boolean;
  selectedCase?: DigitalCase;
  showContextQuestion: boolean;
  contextQuestions: DigitalQuestion[];
  questions: DigitalQuestion[];
  enrollment?: Enrollment;
  selectedSection?: DigitalSection;
  trialId?: number;
  contextSubmissionMapByQuestionId: Record<number, DigitalSubmission | undefined>;
  submissionsMapByQuestionAndCaseId: Record<number, Record<number, DigitalSubmission> | undefined>;
  settingsDictionary: { get: (qId: number, cId: number) => QuestionCaseSetting | undefined };
  highlightedQuestion?: number;
  disabledSurveyInput: boolean;
  onAddOrUpdateDigitalSubmission: (submission: DigitalSubmission) => void;
  setHilightedQuestion?: React.Dispatch<React.SetStateAction<number | undefined>>
  showErrorNotification: (message: string) => void;
  trackSavedStatus: (isSaved: boolean) => void;
}

const BootstrapQuestionForm: React.FC<BootstrapQuestionFormProps> = ({
  isPublicView,
  showContextQuestion,
  selectedCase,
  contextQuestions,
  questions,
  enrollment,
  selectedSection,
  trialId,
  contextSubmissionMapByQuestionId,
  submissionsMapByQuestionAndCaseId,
  highlightedQuestion,
  settingsDictionary,
  disabledSurveyInput,
  setHilightedQuestion,
  onAddOrUpdateDigitalSubmission,
  showErrorNotification,
  trackSavedStatus,
}: BootstrapQuestionFormProps) => {
  const trackFocusAndPreventClose = usePreventUnsavedTabClose();

  const saveStatusCallback = useCallback((op: QuestionSaveOp, qId: number, cId?: number) => {
    const trackMap = trackFocusAndPreventClose(op, qId, cId);
    const isSaving = Object.values(trackMap).find((v) => v === 'debounceStarting' || v === 'saving');
    trackSavedStatus(!isSaving);
  }, [trackFocusAndPreventClose, trackSavedStatus]);

  if (!selectedCase && !showContextQuestion) {
    return null;
  }

  if (isPublicView) {
    return (
      <QuestionInputFormPreview
        questions={showContextQuestion ? contextQuestions : questions}
        caseId={selectedCase ? selectedCase.id : undefined}
        settingsDictionary={settingsDictionary}
      />
    );
  }

  if (!enrollment || !selectedSection) {
    return null;
  }

  if (showContextQuestion && trialId) {
    return (
      <ContextQuestionInputForm
        trialId={trialId}
        sectionId={selectedSection.id}
        enrollmentId={enrollment.id}
        questions={contextQuestions}
        contextSubmissionByQuestionId={contextSubmissionMapByQuestionId}
        addOrUpdateDigitalSubmission={onAddOrUpdateDigitalSubmission}
        disabled={disabledSurveyInput}
        hilightedQuestionId={highlightedQuestion}
        setHilightedQuestion={setHilightedQuestion}
        showErrorNotification={showErrorNotification}
        getSaveStatus={saveStatusCallback}
        saveOnBlur
      />
    );
  }

  if (!selectedCase) {
    return null;
  }

  return (
    <QuestionInputForm
      trialId={Number(trialId)}
      caseId={selectedCase.id}
      sectionId={selectedSection.id}
      enrollmentId={enrollment.id}
      questions={questions}
      submissionsMapByQuestionAndCaseId={submissionsMapByQuestionAndCaseId}
      addOrUpdateDigitalSubmission={onAddOrUpdateDigitalSubmission}
      disabled={disabledSurveyInput}
      hilightedQuestionId={highlightedQuestion}
      settingsDictionary={settingsDictionary}
      setHilightedQuestion={setHilightedQuestion}
      showErrorNotification={showErrorNotification}
      getSaveStatus={saveStatusCallback}
      saveOnBlur
    />
  );
};

const QuestionnaireExhibitA = ({
  trial,
  showEnrollmentsSubmissions,
  isEditorPrivileged,
  userRole,
  selectedCase,
  cases,
  questions,
  contextQuestions,
  selectedSection,
  sections,
  submissionsMapByQuestionAndCaseId,
  contextSubmissionMapByQuestionId,
  sectionSubmissionMeta,
  surveySubmissionMeta,
  questionCaseSettings,
  settingsDictionary,
  fullSubmissionMap,
  fullCtxSubmissionMap,
  digitalSubmissionMetas,
  showContextQuestion,
  enrollment,
  isPublicView,
  disabled: disabledSurveyInput,
  setShowContextQuestion,
  optOutFromSection,
  onAddOrUpdateDigitalSubmission,
  setSelectedDigitalSection,
  setSelectedCase,
  finalizeSubmissions,
  showErrorNotification,
}: Props): JSX.Element => {
  const [highlightedQuestion, setHighlightedQuestion] = useState<number>();
  const [highlightOptOut, setHighlightOptOut] = useState<boolean>(false);
  const [submissionPrompt, showSubmissionPrompt] = useState<boolean>(false);
  const [displayReview, setDisplayReview] = useState<boolean>(false);
  // marks if any question is in saving process
  const [allSaved, setAllSaved] = useState<boolean>(true);

  const navigateToQuestionHandler = useCallback((qId: number, cId: number) => {
    setSelectedCase(cId);
    setDisplayReview(false);
    showSubmissionPrompt(false);
    setHighlightedQuestion(qId);
  }, [setSelectedCase]);

  const navigateToCtxQuestionHandler = useCallback((qId: number) => {
    setSelectedCase(undefined);
    setShowContextQuestion(true);
    setHighlightedQuestion(qId);
    setDisplayReview(false);
    showSubmissionPrompt(false);
  }, [setSelectedCase, setShowContextQuestion]);

  const highlightOptOutHandler = useCallback(() => {
    setDisplayReview(false);
    showSubmissionPrompt(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    setHighlightOptOut(true);
  }, []);

  const onFinalizeSubmissionClick = () => {
    showSubmissionPrompt(true);
  };

  const onFinalizeSubmissionPromptConfirm = useCallback(() => {
    finalizeSubmissions().finally(() => {
      showSubmissionPrompt(false);
    });
  }, [finalizeSubmissions]);

  const onReviewClick = useCallback(() => {
    setDisplayReview(true);
  }, []);

  const handleReviewTabSwitch = useCallback((
    event: React.SyntheticEvent | null, newValue: number,
  ) => {
    setSelectedDigitalSection(newValue);
  }, [setSelectedDigitalSection]);

  const caseHeading = selectedCase?.name
    || (showContextQuestion ? <FormattedMessage id="review.methodicalQuestionTitle" /> : '');

  return (
    <>
      <Box display="flex" sx={{ height: '100%' }}>
        <Paper
          variant="outlined"
          sx={{
            width: '60vw',
            m: 2,
          }}
        >
          <Box sx={{ m: '1rem 1.5rem 0 1.5rem' }} display="flex" justifyContent="space-between" alignItems="center">
            <SubHeadingTitle title={selectedSection?.name} />
            {selectedSection?.canOptOut && isPublicView && (<SectionOptOutPreview />)}
            {
              selectedSection?.canOptOut && sectionSubmissionMeta
              && trial && enrollment && (
                <SectionOptOut
                  trial={trial}
                  enrollment={enrollment}
                  sectionSubmissionMeta={sectionSubmissionMeta}
                  selectedSection={selectedSection}
                  showSubmissions={showEnrollmentsSubmissions}
                  optOutFromSection={optOutFromSection}
                  highlightOptOut={highlightOptOut}
                  setHighlightOptOut={setHighlightOptOut}
                  isEditorPrivileged={isEditorPrivileged}
                />
              )
            }
          </Box>
          <Box sx={{ m: '0 1.5rem 0 1.5rem' }}>
            <SubHeadingTitle title={caseHeading} />
          </Box>
          <Box aria-label="reference-link-container">
            {
              selectedSection && selectedSection.referenceLinks && (
                <Box sx={{
                  mx: 2,
                  mt: 2,
                  p: 2,
                  bgcolor: 'grey.100',
                }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Reference Links
                  </Typography>
                  <ReferenceLinks
                    selectedSection={selectedSection}
                  />
                </Box>
              )
            }
          </Box>
          <BootstrapQuestionForm
            contextQuestions={contextQuestions}
            contextSubmissionMapByQuestionId={contextSubmissionMapByQuestionId}
            onAddOrUpdateDigitalSubmission={onAddOrUpdateDigitalSubmission}
            isPublicView={isPublicView}
            questions={questions}
            showContextQuestion={showContextQuestion}
            submissionsMapByQuestionAndCaseId={submissionsMapByQuestionAndCaseId}
            enrollment={enrollment}
            selectedCase={selectedCase}
            selectedSection={selectedSection}
            trialId={trial?.id}
            highlightedQuestion={highlightedQuestion}
            setHilightedQuestion={setHighlightedQuestion}
            settingsDictionary={settingsDictionary}
            disabledSurveyInput={disabledSurveyInput}
            showErrorNotification={showErrorNotification}
            trackSavedStatus={setAllSaved}
          />
        </Paper>
        <Paper
          variant="outlined"
          sx={{
            height: 'fit-content',
            width: 300,
            position: 'sticky',
            m: '1rem 0 1rem 0',
            top: 16,
          }}
        >
          <SectionCaseSelector
            sections={sections}
            selectedSection={selectedSection}
            cases={cases}
            selectedCase={selectedCase}
            selectDigitalSection={setSelectedDigitalSection}
            selectDigitalCase={setSelectedCase}
            onSubmitClick={onFinalizeSubmissionClick}
            onReviewClick={onReviewClick}
            disabled={showEnrollmentsSubmissions || surveySubmissionMeta?.finalized || !enrollment}
            hasContextQuestions={contextQuestions.length > 0}
            setShowContextQuestion={setShowContextQuestion}
            showContextQuestion={showContextQuestion}
          />
        </Paper>
      </Box>
      {/* Submission Dialog */}
      <GenericDialog maxWidth="xl" isOpen={submissionPrompt} onRequestClose={() => showSubmissionPrompt(false)}>
        <SubmissionDialog
          cases={cases}
          questions={questions}
          contextQuestions={contextQuestions}
          questionCaseSubmissionMap={submissionsMapByQuestionAndCaseId}
          questionCaseSettings={questionCaseSettings}
          fullSubmissionMap={fullSubmissionMap}
          fullCtxSubmissionMap={fullCtxSubmissionMap}
          onCaseClick={navigateToQuestionHandler}
          onSubmit={onFinalizeSubmissionPromptConfirm}
          sections={sections}
          selectedSection={selectedSection}
          settingsDictionary={settingsDictionary}
          onSectionClick={handleReviewTabSwitch}
          onNavigateCtxQuestion={navigateToCtxQuestionHandler}
          onHighlightOptout={highlightOptOutHandler}
          dsms={digitalSubmissionMetas}
          isAllSaved={allSaved}
        />
      </GenericDialog>
      {/* Submission Review Dialog */}
      <GenericDialog
        isOpen={displayReview}
        maxWidth="xl"
        onRequestClose={() => {
          setDisplayReview(false);
        }}
      >
        {
          trial && (
            <SubmissionReviewDialog
              trialId={trial.id}
              enrollment={enrollment}
              userRole={userRole}
              showSubmissions={showEnrollmentsSubmissions}
              isPublicView={isPublicView}
              displayReview={displayReview}
              selectedSection={selectedSection}
              sections={sections}
              cases={cases}
              questions={questions}
              contextQuestions={contextQuestions}
              questionCaseSettings={questionCaseSettings}
              settingsDictionary={settingsDictionary}
              questionCaseSubmissionMap={submissionsMapByQuestionAndCaseId}
              contextSubmissionMapByQuestionId={contextSubmissionMapByQuestionId}
              fullSubmissionMap={fullSubmissionMap}
              fullCtxSubmissionMap={fullCtxSubmissionMap}
              dsms={digitalSubmissionMetas}
              onSectionClick={handleReviewTabSwitch}
              onCaseClick={navigateToQuestionHandler}
              onNavigateCtxQuestion={navigateToCtxQuestionHandler}
              onHighlightOptout={highlightOptOutHandler}
            />
          )
        }
      </GenericDialog>
    </>
  );
};

export default QuestionnaireExhibitA;
